/**
 * autodocCommon.ts - briques partagées pour tous les documents AutoDoc
 * (Plateforme M365, Azure, Politique de sécurité, Registre des traitements).
 * Garantit une mise en forme homogène : logo, page de garde, introduction,
 * tableaux, styles de titres.
 */
import {
  Paragraph, Table, TableRow, TableCell, TextRun, ImageRun, TableOfContents,
  HeadingLevel, WidthType, AlignmentType, BorderStyle,
} from 'docx';

export const BRAND = '1F3D5C';
export const ACCENT = '2E8B96';

// Police sans-serif (Arial ≈ Helvetica du PDF) pour uniformiser Word et PDF.
export const REPORT_FONT = 'Arial';

export const AUTODOC_STYLES = {
  default: { document: { run: { font: REPORT_FONT, size: 20 } } },
  paragraphStyles: [
    { id: 'Heading1', name: 'Heading 1', basedOn: 'Normal', next: 'Normal', run: { font: REPORT_FONT, size: 30, bold: true, color: BRAND }, paragraph: { spacing: { before: 240, after: 120 } } },
    { id: 'Heading3', name: 'Heading 3', basedOn: 'Normal', next: 'Normal', run: { font: REPORT_FONT, size: 24, bold: true, color: ACCENT }, paragraph: { spacing: { before: 160, after: 80 } } },
  ],
};

export function fmtDate(d?: string): string {
  if (!d) return '';
  const x = new Date(d);
  return isNaN(x.getTime()) ? '' : x.toLocaleDateString('fr-BE');
}
export function today(): string { return new Date().toLocaleDateString('fr-BE'); }
export function safeName(s: string): string { return (s || 'organisation').replace(/[^\w]+/g, '_'); }

export function statusFr(s?: string): string {
  const v = (s ?? '').toLowerCase();
  if (v === 'pass') return 'Conforme';
  if (v === 'fail' || v === 'error') return 'Non conforme';
  if (v === 'warning' || v === 'warn') return 'À améliorer';
  if (v === 'manual' || v === 'skipped') return 'À vérifier (manuel)';
  if (v === 'not_applicable' || v === 'na') return 'Non applicable';
  return 'Non évalué';
}
export function statusColor(s?: string): string {
  const v = (s ?? '').toLowerCase();
  if (v === 'pass') return '1F7A45';
  if (v === 'fail' || v === 'error') return 'B82329';
  if (v === 'warning' || v === 'warn') return 'A86317';
  if (v === 'manual' || v === 'skipped') return '4A4A6E';
  return '7A7A7A';
}

export function cell(text: string, opts?: { bold?: boolean; color?: string; width?: number }): TableCell {
  return new TableCell({
    width: opts?.width ? { size: opts.width, type: WidthType.PERCENTAGE } : undefined,
    children: [new Paragraph({ children: [new TextRun({ text: text ?? '', bold: opts?.bold, color: opts?.color, size: 18 })] })],
  });
}
export function headerCell(text: string, width?: number): TableCell {
  return new TableCell({
    width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined,
    shading: { fill: BRAND },
    children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 18 })] })],
  });
}

/**
 * Rasterise N'IMPORTE quel data URL image (png/jpeg/svg/webp/gif) en PNG via
 * canvas, redimensionné à maxW. Robuste : renvoie null si l'image ne charge pas.
 * (docx ne sait pas embarquer du SVG -> on passe toujours par du PNG.)
 */
async function rasterizeToPng(dataUrl: string, maxW: number): Promise<{ bytes: Uint8Array; w: number; h: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const natW = img.naturalWidth || maxW;
        const natH = img.naturalHeight || maxW;
        const scale = Math.min(1, maxW / natW);
        const w = Math.max(1, Math.round(natW * scale));
        const h = Math.max(1, Math.round(natH * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0, w, h);
        const b64 = canvas.toDataURL('image/png').split(',')[1];
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        resolve({ bytes, w, h });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/**
 * Construit un ImageRun pour le logo. Corrige le bug docx 8.x (ImageRun exige
 * `type`) qui faisait échouer silencieusement l'affichage du logo, et gère le SVG.
 */
export async function logoImageRun(dataUrl?: string | null, maxW = 150): Promise<ImageRun | null> {
  if (!dataUrl) return null;
  const r = await rasterizeToPng(dataUrl, maxW);
  if (!r) return null;
  try {
    // Cette version de docx infère le format depuis les octets (PNG ici) : pas de `type`.
    return new ImageRun({ data: r.bytes, transformation: { width: r.w, height: r.h } });
  } catch { return null; }
}

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_BORDERS = { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER, insideHorizontal: NO_BORDER, insideVertical: NO_BORDER };

/**
 * Page de garde UNIFIÉE (identique sur tous les rapports) :
 *   - bandeau de marque NIS365 pleine largeur (couleur BRAND)
 *   - logo de l'organisation (si fourni) centré
 *   - titre + sous-titre
 *   - encadré bordé (ACCENT) : nom de l'organisation + lignes optionnelles + date
 *   - note de bas de page
 * `boxLines` permet d'ajouter des champs dans l'encadré (ex : ['Niveau cible', 'Important']).
 */
export async function buildCover(opts: {
  brandLine: string; title: string; subtitle: string; orgName: string;
  logo?: string | null; footnote?: string; boxLines?: [string, string][];
}): Promise<(Paragraph | Table)[]> {
  const out: (Paragraph | Table)[] = [];

  // 1. Bandeau de marque (bande pleine largeur BRAND)
  out.push(new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: NO_BORDERS,
    rows: [new TableRow({ children: [new TableCell({
      shading: { fill: BRAND },
      margins: { top: 140, bottom: 140, left: 220, right: 220 },
      children: [new Paragraph({ children: [
        new TextRun({ text: 'NIS365', bold: true, color: 'FFFFFF', size: 30 }),
        new TextRun({ text: ' AUDITOR', color: '9BC7D6', size: 26 }),
      ] })],
    })] })],
  }));

  out.push(new Paragraph({ children: [] }), new Paragraph({ children: [] }), new Paragraph({ children: [] }));

  // 2. Logo de l'organisation (centré) si présent
  const logo = await logoImageRun(opts.logo, 140);
  if (logo) {
    out.push(new Paragraph({ alignment: AlignmentType.CENTER, children: [logo] }));
    out.push(new Paragraph({ children: [] }));
  }

  // 3. Titre + sous-titre
  out.push(
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: opts.title, bold: true, size: 52, color: BRAND })] }),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: opts.subtitle, size: 26, color: '555555' })] }),
    new Paragraph({ children: [] }),
    new Paragraph({ children: [] }),
  );

  // 4. Encadré bordé : organisation + lignes optionnelles + date
  const boxChildren: Paragraph[] = [
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: opts.orgName, bold: true, size: 30, color: '142939' })] }),
    new Paragraph({ children: [] }),
  ];
  for (const [k, v] of (opts.boxLines ?? [])) {
    boxChildren.push(new Paragraph({ alignment: AlignmentType.CENTER, children: [
      new TextRun({ text: `${k} : `, color: '3A5470', size: 20 }),
      new TextRun({ text: v, bold: true, size: 20, color: '142939' }),
    ] }));
  }
  boxChildren.push(new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `Généré le : ${today()}`, size: 20, color: '666666' })] }));

  const boxBorder = { style: BorderStyle.SINGLE, size: 6, color: ACCENT };
  out.push(new Table({
    width: { size: 68, type: WidthType.PERCENTAGE },
    alignment: AlignmentType.CENTER,
    rows: [new TableRow({ children: [new TableCell({
      borders: { top: boxBorder, bottom: boxBorder, left: boxBorder, right: boxBorder },
      margins: { top: 220, bottom: 220, left: 200, right: 200 },
      children: boxChildren,
    })] })],
  }));

  // 5. Note de bas de page
  if (opts.footnote) {
    out.push(new Paragraph({ children: [] }));
    out.push(new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: opts.footnote, italics: true, size: 18, color: '888888' })] }));
  }
  return out;
}

/** Table des matières Word (mise à jour à l'ouverture — voir features.updateFields). */
export function buildToc(): (Paragraph | TableOfContents)[] {
  return [
    new Paragraph({ text: 'Sommaire', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    new Paragraph({ children: [new TextRun({ text: 'Clic droit → « Mettre à jour les champs » pour actualiser la pagination.', italics: true, size: 16, color: '888888' })] }),
    new TableOfContents('Sommaire', { hyperlink: true, headingStyleRange: '1-3' }),
  ];
}

/** Bloc "Introduction" homogène : objectif + public cible + périmètre optionnel. */
export function buildIntro(opts: { purpose: string; audience: string; scope?: string }): (Paragraph | Table)[] {
  const rows: [string, string][] = [
    ['Objet du document', opts.purpose],
    ['Public cible', opts.audience],
  ];
  if (opts.scope) rows.push(['Périmètre', opts.scope]);
  return [
    new Paragraph({ text: 'Introduction', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: rows.map(([k, v]) => new TableRow({ children: [headerCell(k, 26), cell(v)] })) }),
    new Paragraph({ children: [] }),
  ];
}
